import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Box, MenuItem, Popover, Stack, Typography, useMediaQuery } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import dayjs from 'dayjs';
import React from 'react';
import ChatFileIcon from 'src/components/ChatFileIcon/view';
import { DownloadFileIcon } from 'src/components/DownloadFileIcon';
import { formatUnicodeStylesToSafeHtml } from 'src/components/TemplateText/helpers';
import { FileLinkWithMimeType, InboxContactChatMessage } from 'src/constants/types';
import { ChatMessageActionButton } from './ChatMessageActionButton.styles';
import { ChatMessageBubble } from './ChatMessageBubble.styles';
import { ChatMessageContainer } from './ChatMessageContainer.styles';
import { ChatMessageSubject } from './ChatMessageSubject.styles';
import { ChatMessageText } from '../ChatMessageText';
import { ChatMessageTimestamp } from './ChatMessageTimestamp.styles';

type MessageProps = {
  message: InboxContactChatMessage;
  showErrorDetails: boolean;
  hideDemoFails: boolean;
  markMessageAsUnread: (message: InboxContactChatMessage) => void;
  hoverMessageId: string | undefined;
  setHoverMessageId: React.Dispatch<React.SetStateAction<string | undefined>>;
};

const Message = ({
  message,
  showErrorDetails,
  hideDemoFails,
  markMessageAsUnread,
  hoverMessageId,
  setHoverMessageId,
}: MessageProps) => {
  const isMobile = useMediaQuery<Theme>(t => t.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const menuOpen = Boolean(anchorEl);
  const isDemoFail = hideDemoFails && message.errorCode === '555';
  const isIncoming = message.sender === 'contact';
  const isFailed = message.status === 'failed' && !isDemoFail;
  const isScheduled = Boolean(
    message.sender === 'company' && message.dateScheduled && message.status === 'pending',
  );
  const hasActions = message.isRead || isScheduled;
  const messageDate = message.dateScheduled ?? message.dateCreated;

  return (
    <ChatMessageContainer
      onMouseEnter={() => setHoverMessageId(message.id)}
      onMouseLeave={() => setHoverMessageId(undefined)}
    >
      <Stack direction="row" justifyContent={isIncoming ? 'flex-start' : 'flex-end'} alignItems="center" spacing={1}>
        {isFailed && !isIncoming && <ErrorOutlineIcon color="error" />}
        <ChatMessageBubble $incoming={isIncoming}>
          <Box position="relative">
            <Box position="absolute" top={-10} right={-10}>
              <DownloadFileIcon
                attachments={message.linksWithMimeType || []}
                isVisible={message.id === hoverMessageId}
                isMobile={isMobile}
              />
            </Box>
            {message.linksWithMimeType?.map((link: FileLinkWithMimeType, index) => (
              <ChatFileIcon url={link.url} mimeType={link.mimeType} key={index} />
            ))}
          </Box>
          {message.subject && message.messageType === 'mms' && (
            <ChatMessageSubject
              $hasText={Boolean(message.text)}
              typography="body2"
              color={isIncoming ? 'common.white' : 'text.primary'}
            >
              {message.subject}
            </ChatMessageSubject>
          )}
          <ChatMessageText
            component="div"
            typography="body2"
            color={isIncoming ? 'common.white' : 'text.primary'}
            dangerouslySetInnerHTML={{ __html: formatUnicodeStylesToSafeHtml(message.text) }}
          />
          <ChatMessageTimestamp variant="caption">
            {(isScheduled ? 'Sent at ' : '') + (messageDate ? dayjs(messageDate).format('YYYY-MM-DD hh:mm A') : '')}
          </ChatMessageTimestamp>
        </ChatMessageBubble>
        {isFailed && isIncoming && <ErrorOutlineIcon color="error" />}
        {hasActions && (
          <>
            <ChatMessageActionButton
              $visible={message.id === hoverMessageId || isMobile || menuOpen}
              aria-label="Message actions"
              onClick={event => setAnchorEl(event.currentTarget)}
            >
              <MoreVertIcon />
            </ChatMessageActionButton>
            <Popover
              id={menuOpen ? `message-context-menu-${message.id}` : undefined}
              open={menuOpen}
              anchorEl={anchorEl}
              onClose={() => setAnchorEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
              {message.isRead && (
                <MenuItem onClick={() => {
                  markMessageAsUnread(message);
                  setAnchorEl(null);
                }}>
                  Mark as Unread
                </MenuItem>
              )}
            </Popover>
          </>
        )}
      </Stack>
      {isFailed && (
        <Typography
          variant="body2"
          color="text.secondary"
          textAlign={isIncoming ? 'left' : 'right'}
          mt={0.5}
          mr={!isIncoming && hasActions ? 6 : 0}
        >
          {showErrorDetails
            ? message.errorCode
              ? message.errorTitle
              : 'Not Delivered (Unknown)'
            : 'Not Delivered'}
        </Typography>
      )}
    </ChatMessageContainer>
  );
};

export default Message;
